<template>
  <teleport to="body">
    <div v-if="open" class="modal" @click.self.prevent="openModal = !openModal">
      <div class="container">
        <font-awesome-icon icon="fa-xmark" class="exit" @click="$emit('close', false)" />
        <div class="product-container">
          <h3>Do you have everything you want...</h3>
          <div class="items" v-for="product in cartProducts" :key="product">
            <img v-if="product.image" :src="product.image" alt="image of product" />
            <div v-else class="no-image">Image coming soon...</div>
            <div class="content">
              <h4>{{ product.name }}</h4>
              <CurrencyFormatter :value="product.price" :currency="currency" />
              <p v-if="product.modifier_list.length > 0">Modifiers:</p>
              <ul>
                <li v-for="modifier in product.modifier_list" :key="modifier">{{ modifier.name }}</li>
              </ul>
            </div>
            <div class="quantity-controls">
              <font-awesome-icon icon="minus-circle" class="quantity-btn" @click="decreaseQuantity(product.hash)" />
              <span class="quantity">{{ product.quantity }}</span>
              <font-awesome-icon icon="plus-circle" class="quantity-btn" @click="increaseQuantity(product.hash)" />
            </div>
          </div>
          <div class="action-btns">
            <button class="clear-btn" @click.stop.prevent="clearCartAndClose">
              <font-awesome-icon icon="fa-solid fa-trash" />
              Start Over
            </button>
            <button class="pay-btn" @click.stop.prevent="sendToEftpos(total)"><CurrencyFormatter :value="total" :currency="currency" /> Pay with EFTPOS</button>
          </div>
        </div>
      </div>
      <div v-if="eftposBusy" id="eftpos-spinner">
        <LoadingSpinner :spinnerMessage="'Please use card reader to complete you order'" />
      </div>
    </div>
  </teleport>
</template>

<script>
import { LoadingSpinner, CurrencyFormatter } from "@monolith/components";
import { mapGetters, mapActions } from "vuex";
import eftposHelpers from "@/components/_mixins/eftposHelpers";
export default {
  name: "KioskCheckout",
  components: {
    LoadingSpinner,
    CurrencyFormatter,
  },
  props: ["open", "close"],
  emits: ["close"],
  mixins: [eftposHelpers],
  data() {
    return {
      openModal: false,
      splitOrderCurrentTotal: null,
      eftposBusy: false,
    };
  },

  mounted() {},

  methods: {
    ...mapActions(["addToCart", "increaseCartQuantity", "decreaseCartQuantity", "addPayment", "clearCart", "clearPayments", "changeLineItemQuantity"]),

    increaseQuantity(productHash) {
      this.changeLineItemQuantity({ productHash: productHash, delta: 1 });
    },

    decreaseQuantity(productHash) {
      this.changeLineItemQuantity({ productHash: productHash, delta: -1 });
    },

    clearCartAndClose() {
      this.clearCart();
      this.clearPayments();
      this.$notify({
        text: "Cart cleared successfully.",
        type: "success",
        duration: 1500,
      });
      this.$emit("close", true);
    },

    sendToEftpos(value) {
      if (window.android || window.webkit) {
        this.eftposBusy = true;
        this.handleEftposPayment(value);
      } else {
        this.$notify({
          text: "This device does not support EFTPOS payments.",
          type: "error",
          duration: 1500,
        });
      }
    },

    completeTransaction() {
      this.eftposBusy = false;
      this.clearCart();
      this.clearPayments();
      this.$notify({
        text: "Your order has been completed successfully.",
        type: "success",
        duration: 1500,
      });
      this.$router.push({ path: "/startOrder" });
    },
  },

  computed: {
    ...mapGetters({
      getKiosk: "getKiosk",
      getKioskOrderDetails: "getKioskOrderDetails",
      cartProducts: "cartProducts",
      eftposSettings: "eftposSettings",
      currency: "currency",
    }),

    total() {
      return this.cartProducts.reduce((acc, product) => acc + product.price * product.quantity, 0).toFixed(2);
    },
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@use "@monolith/styles";

.modal {
  .container {
    border-radius: 1rem;
    width: 50%;
  }
  #eftpos-spinner {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-colour);
    border-radius: 1rem;
    box-shadow: var(--box-shadow);
    opacity: 0.8;
  }

  .product-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    overflow: auto;
    padding: 1rem;
    .items {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      padding: 1rem;
      border-radius: 1rem;
      background-color: var(--primary-colour);
      margin: 0.5rem;
      width: calc(100% - 3rem);
      box-shadow: var(--box-shadow);

      .content {
        display: flex;
        flex-direction: column;
        height: 8rem;
        width: 100%;
        overflow-y: auto;
        ul {
          margin: 0;
          li {
            list-style: circle;
            margin: 0.25rem 0;
          }
        }
      }

      .quantity-controls {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        min-width: 5rem;

        .quantity-btn {
          font-size: 1.5rem;
          color: var(--action-colour);
          cursor: pointer;
        }

        .quantity {
          font-weight: bold;
          font-size: 1.2rem;
          color: var(--text-colour);
          text-align: center;
          min-width: 2rem;
        }
      }
      h4,
      p {
        margin: 0.25rem;
        color: var(--action-colour);
      }

      img,
      .no-image {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        width: 8rem;
        height: 8rem;
        object-fit: cover;
        margin: 0.5rem;
        border-radius: 0.5rem;
        background-color: var(--action-colour);
        color: var(--primary-colour);
      }
    }
    .action-btns {
      display: flex;
      gap: 1rem;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      .clear-btn {
        width: 20rem;
        height: 5rem;
        white-space: nowrap;
        padding: 1rem;
        border-radius: 0.5rem;
        border: 1px solid var(--warning-colour);
        background-color: var(--secondary-colour);
        color: var(--warning-colour);
        font-weight: bold;
        font-size: 1.2rem;
        box-shadow: var(--box-shadow);
        gap: 0.5rem;
        cursor: pointer;
      }
      .pay-btn {
        width: 20rem;
        height: 5rem;
        white-space: nowrap;
        padding: 1rem;
        border-radius: 0.5rem;
        border: 1px solid var(--action-colour);
        background-color: var(--secondary-colour);
        color: var(--action-colour);
        font-weight: bold;
        font-size: 1.2rem;
        box-shadow: var(--box-shadow);
        gap: 0.5rem;
        cursor: pointer;
      }
    }
  }
}
</style>
